import React from 'react'
import { BASE_URL } from '../utilities/constants'

/**
 * Helper para iniciar y limpiar el timer de reservación.
 */

/**
 * Minutos que el kiosko expira antes que el backend (sesión/carrito según lifespan en v3/settings).
 * Evita pagos con PinPad cuando la API ya invalidó el carrito ("Cart has expired" en confirm).
 */
const BOOKING_TIMER_BACKEND_BUFFER_MINUTES = 4

/** Duración del timer de reserva. Si existe `REACT_APP_BOOKING_LIFESPAN_SECONDS` (entero > 0), ignora API y minutos por defecto (solo pruebas). */
function getBookingTimerDurationMs (settings) {
  const sec = parseInt(String(process.env.REACT_APP_BOOKING_LIFESPAN_SECONDS ?? '').trim(), 10)
  if (Number.isFinite(sec) && sec > 0) {
    return sec * 1000
  }
  const minutes = (settings && settings.lifespan) ? settings.lifespan : 10
  const effectiveMinutes = Math.max(1, minutes - BOOKING_TIMER_BACKEND_BUFFER_MINUTES)
  return effectiveMinutes * 60 * 1000
}

/**
 * Inicia el timer de reservación.
 * @param {Object} params
 * @param {Object} params.settings - Configuración global (puede contener lifespan en minutos).
 * @param {Function} params.setupTimer - Función del contexto useTimer para configurar el timer.
 * @param {Function} params.showTimer - Función para mostrar el componente visual del timer.
 * @param {Function} params.setDeadline - Setter (atom) para guardar el deadline ISO.
 * @param {Function} params.setSource - Setter (atom) para guardar el origen (round-search | single-review | other).
 * @param {Function} params.onTimeout - Callback al expirar.
 * @param {string} params.source - Identificador del origen que inicia el timer.
 * @returns {string} deadline ISO generado.
 */
export function startBookingTimer({
  settings,
  setupTimer,
  showTimer,
  setDeadline,
  setSource,
  onTimeout,
  source = 'unknown'
}) {
  const deadline = new Date(Date.now() + getBookingTimerDurationMs(settings)).toISOString()
  setDeadline(deadline)
  setSource && setSource(source)
  setupTimer(deadline, onTimeout, () => {
    showTimer()
  })
  return deadline
}

/**
 * Limpia el timer de reservación.
 * @param {Object} params
 * @param {Function} params.hideTimer - Función para ocultar el timer visual.
 * @param {Function} params.setDeadline - Setter (atom) para limpiar el deadline.
 * @param {Function} [params.setSource] - Setter (atom) opcional para limpiar el origen.
 */
export function clearBookingTimer({
  hideTimer,
  setDeadline,
  setSource
}) {
  hideTimer && hideTimer()
  setDeadline('')
  setSource && setSource('')
}

// Nuevo helper reutilizable para manejar expiraciones
export function handleBookingTimeout({
  hideTimer,
  setDeadline,
  setupModal,
  showModal,
  navigate,
  redirectTo = `${BASE_URL}/`,
  message,
  title = 'Tu reserva expiró',
  positiveTitle = 'Aceptar',
  hideModal,
  beforeNavigate,   // función opcional a ejecutar antes de navegar
  afterNavigate,    // función opcional a ejecutar después de navegar
}) {
  hideTimer && hideTimer()
  setDeadline && setDeadline('')
  const defaultMessage = '¡Lo sentimos! 😓 El tiempo para continuar con tu compra terminó. Vuelve a iniciar tu proceso cuando estés listo.'
  setupModal({
    title,
    content: <p>{ message || defaultMessage }</p>,
    actions: {
      positive: {
        title: positiveTitle,
        onClick: () => {
          beforeNavigate && beforeNavigate()
          navigate && navigate(redirectTo)
          afterNavigate && afterNavigate()
          hideModal && hideModal()
        }
      }
    }
  })
  showModal && showModal()
}
